import { Avatar, AvatarGroup, Box, Tooltip, Typography } from '@mui/material'
import React, { FC, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

type Size = 'xs' | 'sm' | 'md' | 'lg'

const SIZE_PX: Record<Size, number> = {
  xs: 20,
  sm: 28,
  md: 36,
  lg: 48,
}

export interface EntityAvatarGroupMember {
  /** Unique identifier for the entity */
  id?: string
  /** User UUID for navigation */
  userId?: string
  /** Display name */
  name?: string
  /** Photo/avatar URL */
  photoUrl?: string
  /** Optional subtitle shown in tooltip */
  subtitle?: string
  /** Optional role shown in tooltip */
  role?: string
  /** Custom tooltip content (overrides default) */
  tooltipContent?: ReactNode
}

interface EntityAvatarGroupProps {
  /** Array of entities to display */
  members: EntityAvatarGroupMember[]
  /** Avatar size */
  size?: Size
  /** Maximum number of avatars to show before +N */
  max?: number
  /** Spacing between avatars: 'small' | 'medium' or a number */
  spacing?: 'small' | 'medium' | number
  /** Enable click navigation to user profiles */
  enableNav?: boolean
  /** Show tooltips on hover */
  showTooltips?: boolean
  /** Fallback icon when no photo */
  fallbackIcon?: ReactNode
  /** Custom render for tooltip content */
  renderTooltip?: (member: EntityAvatarGroupMember) => ReactNode
  /** Optional className */
  className?: string
  /** Optional styles */
  style?: React.CSSProperties
}

/**
 * A group of entity avatars with individual tooltips and navigation
 */
const EntityAvatarGroup: FC<EntityAvatarGroupProps> = ({
  members,
  size = 'sm',
  max = 4,
  spacing = 'medium',
  enableNav = false,
  showTooltips = true,
  fallbackIcon,
  renderTooltip,
  className,
  style,
}) => {
  const navigate = useNavigate()
  const px = SIZE_PX[size]

  const handleClick = (member: EntityAvatarGroupMember) => (e: React.MouseEvent) => {
    if (enableNav && member.userId) {
      e.stopPropagation()
      navigate(`/teams/profile/${member.userId}`)
    }
  }

  const getInitials = (name?: string): string => {
    if (!name) return '?'
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (!parts.length) return '?'
    const first = parts[0]?.[0] ?? ''
    const last = parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : ''
    return (first + last).toUpperCase()
  }

  const defaultTooltipContent = (member: EntityAvatarGroupMember): ReactNode => (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {member.name || 'Unknown'}
      </Typography>
      {member.role && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {member.role}
        </Typography>
      )}
      {member.subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {member.subtitle}
        </Typography>
      )}
    </Box>
  )

  const renderAvatar = (member: EntityAvatarGroupMember, index: number) => {
    const isClickable = enableNav && !!member.userId
    const initials = getInitials(member.name)

    const avatar = (
      <Avatar
        key={member.id || member.userId || index}
        src={member.photoUrl}
        alt={member.name || 'User'}
        onClick={handleClick(member)}
        sx={{
          width: px,
          height: px,
          fontSize: Math.max(10, Math.floor(px / 2.4)),
          fontWeight: 600,
          cursor: isClickable ? 'pointer' : 'default',
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          // '&:hover': isClickable
          //   ? {
          //       transform: 'scale(1.1)',
          //       zIndex: 10,
          //       boxShadow: 3,
          //     }
          //   : {},
          '&:hover': {
            transform: 'scale(1.1)',
            zIndex: 10,
            boxShadow: 3,
          },
        }}
      >
        {fallbackIcon || initials}
      </Avatar>
    )

    if (showTooltips) {
      const tooltipContent = member.tooltipContent
        ? member.tooltipContent
        : renderTooltip
        ? renderTooltip(member)
        : defaultTooltipContent(member)

      return (
        <Tooltip key={member.id || member.userId || index} title={tooltipContent} arrow>
          {avatar}
        </Tooltip>
      )
    }

    return avatar
  }

  if (!members || members.length === 0) {
    return null
  }

  return (
    <AvatarGroup
      max={max}
      spacing={spacing}
      className={className}
      sx={{
        ...style,
        '& .MuiAvatar-root': {
          width: px,
          height: px,
          fontSize: Math.max(10, Math.floor(px / 2.4)),
          fontWeight: 600,
        },
      }}
      slotProps={{
        additionalAvatar: {
          sx: {
            width: px,
            height: px,
            fontSize: Math.max(10, Math.floor(px / 2.4)),
            fontWeight: 600,
          },
        },
      }}
    >
      {members.map((member, index) => renderAvatar(member, index))}
    </AvatarGroup>
  )
}

export default EntityAvatarGroup
